/** Query param that gives the cover still's media requests their own
 *  browser-cache entry. */
export const STILL_CORS_PARAM = 'ac-still';

// Public media bucket. Its CORS rule only answers requests that carry an
// Origin header, and those plain responses are cached (max-age 4h) with no
// `Vary: Origin`.
const STORAGE_ORIGIN = 'https://storage.autoclipper.live/';

function tagUrl(value: string): string {
  if (!value.startsWith(STORAGE_ORIGIN)) return value;
  const [base, hash] = value.split('#', 2);
  if (new RegExp(`[?&]${STILL_CORS_PARAM}=`).test(base)) return value;
  const tagged = `${base}${base.includes('?') ? '&' : '?'}${STILL_CORS_PARAM}=1`;
  return hash === undefined ? tagged : `${tagged}#${hash}`;
}

/**
 * Rewrites every public-storage URL in the cover still's input props —
 * string values and URL-keyed maps alike (e.g. `fillerFits`), so lookups
 * between them stay consistent.
 *
 * Why: the editor Player plays fillers/media through plain `<video>`
 * elements, whose non-CORS responses the browser caches without CORS
 * headers. `@remotion/web-renderer` then fetches the same URL in CORS mode,
 * gets that cached copy, and the request is blocked ("No
 * 'Access-Control-Allow-Origin' header"). A distinct URL forces a fresh CORS
 * request, which the bucket answers correctly. The Player and export are
 * unaffected — only the still's props are rewritten.
 */
export function withStillCorsCacheKey<T>(value: T): T {
  if (typeof value === 'string') return tagUrl(value) as T;
  if (Array.isArray(value)) return value.map((item) => withStillCorsCacheKey(item)) as T;
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      out[tagUrl(key)] = withStillCorsCacheKey(entry);
    }
    return out as T;
  }
  return value;
}
